import { useEffect, useState } from 'react';

const API_BASE      = 'https://api.uptimerobot.com/v2';
const CACHE_KEY     = 'simpra_uptime_status';
const CACHE_TTL     = 60 * 1000;

// Product name (products.js) -> UptimeRobot Public Status Page friendly_name
const PSP_BY_PRODUCT = {
    pos         : 'Simpra POS',
    inventory   : 'Simpra Inventory',
    link        : 'Simpra Link',
    'c&p'       : 'Simpra Reservation (Check&Place)',
};

const getApiKey = () => window.REACT_APP_UPTIMEROBOT_API_KEY || process.env.REACT_APP_UPTIMEROBOT_API_KEY;

let inflight = null;

const post = async (endpoint, extra = {}, tries = 3) => {
    const apiKey = getApiKey();
    const body   = new URLSearchParams({ api_key: apiKey, format: 'json', ...extra });

    for (let i = 0; i < tries; i++) {
        try {
            const res  = await fetch(`${API_BASE}/${endpoint}`, {
                method  : 'POST',
                headers : { 'Content-Type': 'application/x-www-form-urlencoded' },
                body    : body.toString(),
            });
            const data = await res.json();
            if (data && data.stat === 'ok') return data;
        } catch (e) { /* schema-stub / network hiccup -> retry */ }
    }
    return null;
};

const fetchMonitors = async () => {
    const all = [];
    let offset = 0;
    let total  = 0;

    do {
        const page = await post('getMonitors', { limit: 50, offset });
        if (!page) return all;
        all.push(...page.monitors);
        total   = page.pagination.total;
        offset += 50;
    } while (offset < total);

    return all;
};

// status: 2 = up, 0 = paused (filtered out earlier). all up -> operational, all down -> incident, mixed -> degraded
const computeStatus = monitors => {
    if (!monitors.length)                       return 'UNKNOWN';
    if (monitors.every( m => m.status === 2 ))  return 'OPERATIONAL';
    if (monitors.every( m => m.status !== 2 ))  return 'INCIDENT';
    return 'DEGRADED';
};

const readCache = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { time, data } = JSON.parse(raw);
        if (Date.now() - time > CACHE_TTL) return null;
        return data;
    } catch (e) { return null; }
};

const writeCache = data => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), data }));
    } catch (e) { /* ignore quota */ }
};

const load = async () => {
    const [ psps, monitors ] = await Promise.all([ post('getPSPs'), fetchMonitors() ]);
    if (!psps) return {};

    const byId = {};
    monitors.forEach( m => { byId[m.id] = m; });

    const result = {};
    Object.entries(PSP_BY_PRODUCT).forEach(([ product, name ]) => {
        const psp = psps.psps.find( p => p.friendly_name === name );
        if (!psp) return;

        const ids  = Array.isArray(psp.monitors) ? psp.monitors : [];
        const mons = ids.map( id => byId[id] ).filter( m => m && m.status !== 0 );

        result[product] = {
            statusText  : computeStatus(mons),
            url         : psp.custom_url,
        };
    });

    return result;
};

const getStatuses = () => {
    const cached = readCache();
    if (cached) return Promise.resolve(cached);
    if (inflight) return inflight;

    inflight = load()
        .then( data => { writeCache(data); return data; })
        .finally( () => { inflight = null; });

    return inflight;
};

const useUptimeStatuses = () => {
    const [ statuses, setStatuses ] = useState(() => readCache() || {});

    useEffect(() => {
        if (!getApiKey()) return;

        let mounted = true;
        const run   = () => getStatuses().then( data => { if (mounted && data) setStatuses(data); });

        // lazy: browser bosa cikinca calis, ilk render / sayfa yuklenmesini etkileme
        const idle  = window.requestIdleCallback
            ? window.requestIdleCallback(run, { timeout: 3000 })
            : setTimeout(run, 1500);

        return () => {
            mounted = false;
            if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(idle);
            else clearTimeout(idle);
        };
    }, []);

    return statuses;
};

export default useUptimeStatuses;
